import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Button, Col, FormFeedback, FormGroup, Input, Label,
    Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner,
} from 'reactstrap';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import Flatpickr from 'react-flatpickr';
import { mergeOperationalDateCaps } from '../../../utils/flatpickrOperational';
import { ConditionSuggestionPills } from '../../../utils/deliveryConditionSuggestions';
import SignatureCanvas from 'react-signature-canvas';
import moment from 'moment';

import notify from '../../../custom/helpers/Notify';
import {
    receiveAllRequest,
    forwardAllRequest,
    deliverAllRequest,
    getArrivalFlightListRequest,
    getDepartureFlightListRequest,
} from '../../../api/controller/CaseFileController';

const Field = ({ label, required, children, error }) => (
    <FormGroup className="mb-3">
        <Label className="fw-semibold mb-1" style={{ fontSize: '0.85rem' }}>
            {label}{required && <span className="text-danger ms-1">*</span>}
        </Label>
        {children}
        {error && <FormFeedback className="d-block">{error}</FormFeedback>}
    </FormGroup>
);

const fpOpts = mergeOperationalDateCaps({ dateFormat: 'Y-m-d', altInput: true, altFormat: 'F j, Y' });
const fpOptsDT = mergeOperationalDateCaps({ dateFormat: 'Y-m-d H:i', altInput: true, altFormat: 'F j, Y H:i', enableTime: true, time_24hr: true });

const selectStyles = (themeType) => themeType !== 'dark' ? {} : {
    control:     (b) => ({ ...b, backgroundColor: '#2a2f3d', borderColor: '#3d4354', color: '#e9ecef' }),
    menu:        (b) => ({ ...b, backgroundColor: '#2a2f3d' }),
    option:      (b, s) => ({ ...b, backgroundColor: s.isFocused ? '#3d4354' : '#2a2f3d', color: '#e9ecef' }),
    singleValue: (b) => ({ ...b, color: '#e9ecef' }),
    input:       (b) => ({ ...b, color: '#e9ecef' }),
};

export function bagStatusId(bag) {
    return Number(bag?.bag_status_id ?? bag?.bag_status?.id ?? 0);
}

/** TRACING (1), LOCATED (2), PROCESSING (9) — matches `CaseFileActionController::receiveAll`. */
export function getBagsEligibleForReceiveAll(caseFile) {
    const bags = caseFile?.bags || [];
    return bags.filter(b => [1, 2, 9].includes(bagStatusId(b)));
}

/** RECEIVED (3) — rows updated by forwardAll / deliverAll. API also rejects if any bag still has no receive_date. */
export function getBagsEligibleForForwardOrDeliverAll(caseFile) {
    const bags = caseFile?.bags || [];
    return bags.filter(b => bagStatusId(b) === 3);
}

/** Matches server `bags()->whereNull('receive_date')->exists()` guard on forwardAll / deliverAll. */
export function caseFileHasBagWithoutReceiveDate(caseFile) {
    const bags = caseFile?.bags || [];
    return bags.some(b => b.receive_date == null || b.receive_date === '');
}

/** One neutral pill per bag tag (bulk preview + deliver signature step). */
function BagTagPills({ bags }) {
    if (!bags?.length) return null;
    return (
        <div className="d-flex flex-wrap gap-2">
            {bags.map(b => (
                <span
                    key={b.id}
                    className="badge rounded-pill bg-secondary fw-normal text-white"
                    style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}
                >
                    {b.tag_number != null && String(b.tag_number).trim() !== '' ? b.tag_number.trim() : '—'}
                </span>
            ))}
        </div>
    );
}

function BulkBagsPreview({ bags, themeType, emptyLabel, toBeAction }) {
    const border = themeType === 'dark' ? '#3d4354' : '#e9ecef';
    const bg = themeType === 'dark' ? '#2a2f3d' : '#f8f9fa';

    if (!bags?.length) {
        return (
            <div className="mb-3 p-3 rounded small text-muted" style={{ border: `1px dashed ${border}`, background: bg }}>
                {emptyLabel}
            </div>
        );
    }

    return (
        <div className="mb-4 rounded" style={{ border: `1px solid ${border}`, overflow: 'hidden' }}>
            <div
                className="px-3 py-2 small fw-semibold"
                style={{ background: themeType === 'dark' ? '#1e2330' : '#e9ecef', borderBottom: `1px solid ${border}` }}
            >
                Bags to be {toBeAction} ({bags.length})
            </div>
            <div className="px-3 py-2" style={{ maxHeight: '200px', overflowY: 'auto', background: themeType === 'dark' ? '#252a38' : '#fff' }}>
                <BagTagPills bags={bags} />
            </div>
        </div>
    );
}

// ─── Receive all ─────────────────────────────────────────────────────────────

export const CaseFileReceiveAllModal = ({ caseFile, onClose, onSuccess, themeType }) => {
    const targetBags = getBagsEligibleForReceiveAll(caseFile);
    const [date, setDate] = useState(() => moment().format('YYYY-MM-DD'));
    const [flight, setFlight] = useState(null);
    const [flightOptions, setFlightOptions] = useState([]);
    const [flightLoading, setFlightLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const caseType = caseFile?.type;
    const flightRequired = caseType !== 'OHD' && caseType !== 'UNCLAIMED';

    useEffect(() => {
        setDate(moment().format('YYYY-MM-DD'));
        setFlight(null);
        setFlightOptions([]);
        setErrors({});
    }, [caseFile?.id]);

    useEffect(() => {
        let cancelled = false;
        async function loadFlights() {
            if (!date) {
                setFlightOptions([]);
                setFlightLoading(false);
                return;
            }
            setFlightLoading(true);
            const res = await getArrivalFlightListRequest({ Date: date });
            if (cancelled) return;
            setFlightLoading(false);
            if (res && res.Status !== 'error' && Array.isArray(res.data)) {
                setFlightOptions(res.data.map(row => ({
                    label: row.name,
                    value: row.name,
                })));
            } else {
                setFlightOptions([]);
                if (res?.Status === 'error') {
                    notify('error', res.Message || 'Could not load arrival flights for this date.');
                }
            }
        }
        loadFlights();
        return () => { cancelled = true; };
    }, [date, caseFile?.id]);

    const flightString = flight ? String(flight.value ?? flight.label ?? '').trim() : '';

    const submit = async () => {
        const e = {};
        if (!date) e.Date = 'Date is required.';
        if (flightRequired && !flightString) e.Flight = 'Flight is required for this case type.';
        if (Object.keys(e).length) { setErrors(e); return; }
        setLoading(true);
        const res = await receiveAllRequest({
            id: caseFile.id,
            Date: date,
            Flight: flightString || undefined,
        });
        setLoading(false);
        if (res?.Status === 'success') {
            onSuccess(res);
        } else {
            notify('error', res?.Message || 'Something went wrong.');
        }
    };

    return (
        <Modal isOpen centered>
            <ModalHeader toggle={onClose}>
                <i className="mdi mdi-bag-suitcase me-2 text-info" />
                Receive all bags
            </ModalHeader>
            <ModalBody>
                <div className="mb-3 small text-muted" style={{ marginTop: '-0.25rem' }}>
                    {caseFile?.file_reference && <span className="me-2 fw-semibold">{caseFile.file_reference}</span>}
                    {caseFile?.reference && <span>{caseFile.reference}</span>}
                    {caseType && <span className="ms-2 badge bg-secondary">{caseType}</span>}
                </div>
                <BulkBagsPreview
                    bags={targetBags}
                    themeType={themeType}
                    toBeAction="received"
                    emptyLabel="No bags in TRACING, LOCATED, or PROCESSING — nothing to receive."
                />
                <Row>
                    <Col md={6}>
                        <Field label="Receive date" required error={errors.Date}>
                            <Flatpickr
                                className={`form-control${errors.Date ? ' is-invalid' : ''}`}
                                options={fpOpts}
                                value={date}
                                onChange={([d]) => {
                                    const next = d ? moment(d).format('YYYY-MM-DD') : '';
                                    setDate(next);
                                    setFlight(null);
                                    setErrors({});
                                }}
                                placeholder="Select date"
                            />
                        </Field>
                    </Col>
                    <Col md={6}>
                        <Field label="Received flight" required={flightRequired} error={errors.Flight}>
                            <CreatableSelect
                                classNamePrefix="rs"
                                className={errors.Flight ? 'is-invalid' : ''}
                                inputId="receive-all-flight"
                                options={flightOptions}
                                value={flight}
                                onChange={v => { setFlight(v); setErrors({}); }}
                                placeholder={date ? (flightRequired ? 'Select or type flight…' : 'Optional — select or type') : 'Choose receive date first'}
                                isClearable
                                isDisabled={!date || loading}
                                isLoading={flightLoading}
                                styles={selectStyles(themeType)}
                                formatCreateLabel={i => `Use "${i}"`}
                            />
                        </Field>
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" outline onClick={onClose} disabled={loading}>Cancel</Button>
                <Button color="info" onClick={submit} disabled={loading} style={{ minWidth: '140px' }}>
                    {loading ? <Spinner size="sm" /> : <><i className="mdi mdi-check me-1" />Receive all</>}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

// ─── Forward all ─────────────────────────────────────────────────────────────

export const CaseFileForwardAllModal = ({ caseFile, forwardTypeOptions, onClose, onSuccess, themeType }) => {
    const targetBags = getBagsEligibleForForwardOrDeliverAll(caseFile);
    const blockUntilAllReceived = caseFileHasBagWithoutReceiveDate(caseFile);
    const [form, setForm] = useState({
        Date: moment().format('YYYY-MM-DD'),
        ForwardType: null,
        RushTagNumber: '',
    });
    const [flight, setFlight] = useState(null);
    const [flightOptions, setFlightOptions] = useState([]);
    const [flightLoading, setFlightLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrors(p => ({ ...p, [k]: undefined })); };

    useEffect(() => {
        setForm({
            Date: moment().format('YYYY-MM-DD'),
            ForwardType: null,
            RushTagNumber: '',
        });
        setFlight(null);
        setFlightOptions([]);
        setErrors({});
    }, [caseFile?.id]);

    useEffect(() => {
        let cancelled = false;
        async function loadFlights() {
            if (!form.Date) {
                setFlightOptions([]);
                setFlightLoading(false);
                return;
            }
            setFlightLoading(true);
            const res = await getDepartureFlightListRequest({ Date: form.Date });
            if (cancelled) return;
            setFlightLoading(false);
            if (res && res.Status !== 'error' && Array.isArray(res.data)) {
                setFlightOptions(res.data.map(row => ({
                    label: row.name,
                    value: row.name,
                })));
            } else {
                setFlightOptions([]);
                if (res?.Status === 'error') {
                    notify('error', res.Message || 'Could not load departure flights for this date.');
                }
            }
        }
        loadFlights();
        return () => { cancelled = true; };
    }, [form.Date, caseFile?.id]);

    const flightString = flight ? String(flight.value ?? flight.label ?? '').trim() : '';

    const submit = async () => {
        const e = {};
        if (!form.Date) e.Date = 'Forward date is required.';
        if (!flightString) e.Flight = 'Forward flight is required.';
        if (Object.keys(e).length) { setErrors(e); return; }
        setLoading(true);
        const res = await forwardAllRequest({
            id: caseFile.id,
            Date: form.Date,
            Flight: flightString,
            ForwardType: form.ForwardType?.value,
            RushTagNumber: form.RushTagNumber?.trim() || undefined,
        });
        setLoading(false);
        if (res?.Status === 'success') {
            onSuccess(res);
        } else {
            notify('error', res?.Message || 'Something went wrong.');
        }
    };

    return (
        <Modal isOpen size="lg" centered>
            <ModalHeader toggle={onClose}>
                <i className="mdi mdi-fast-forward me-2 text-warning" />
                Forward all bags
            </ModalHeader>
            <ModalBody>
                <div className="mb-3 small text-muted" style={{ marginTop: '-0.25rem' }}>
                    {caseFile?.file_reference && <span className="me-2 fw-semibold">{caseFile.file_reference}</span>}
                    {caseFile?.reference && <span>{caseFile.reference}</span>}
                    {caseFile?.type && <span className="ms-2 badge bg-secondary">{caseFile.type}</span>}
                </div>
                {blockUntilAllReceived && (
                    <Alert color="warning" className="mb-3 py-2">
                        Receive every bag on this case first. The server will not forward until all bags have a receive date.
                    </Alert>
                )}
                <BulkBagsPreview
                    bags={targetBags}
                    themeType={themeType}
                    toBeAction="forwarded"
                    emptyLabel="No bags in RECEIVED status — forward will not change any bags (other statuses stay as they are)."
                />
                <Row>
                    <Col md={6}>
                        <Field label="Forward date" required error={errors.Date}>
                            <Flatpickr
                                className={`form-control${errors.Date ? ' is-invalid' : ''}`}
                                options={fpOpts}
                                value={form.Date}
                                onChange={([d]) => {
                                    const next = d ? moment(d).format('YYYY-MM-DD') : '';
                                    setForm(p => ({ ...p, Date: next }));
                                    setFlight(null);
                                    setErrors({});
                                }}
                                placeholder="Select date"
                            />
                        </Field>
                    </Col>
                    <Col md={6}>
                        <Field label="Forward flight" required error={errors.Flight}>
                            <CreatableSelect
                                classNamePrefix="rs"
                                className={errors.Flight ? 'is-invalid' : ''}
                                inputId="forward-all-flight"
                                options={flightOptions}
                                value={flight}
                                onChange={v => { setFlight(v); setErrors(p => ({ ...p, Flight: undefined })); }}
                                placeholder={form.Date ? 'Select or type flight…' : 'Choose forward date first'}
                                isClearable
                                isDisabled={!form.Date || loading}
                                isLoading={flightLoading}
                                styles={selectStyles(themeType)}
                                formatCreateLabel={i => `Use "${i}"`}
                            />
                        </Field>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <Field label="Forward type">
                            <Select
                                isClearable
                                options={forwardTypeOptions}
                                value={form.ForwardType}
                                onChange={v => set('ForwardType', v)}
                                placeholder="Select type…"
                                styles={selectStyles(themeType)}
                            />
                        </Field>
                    </Col>
                    <Col md={6}>
                        <Field label="Rush tag number">
                            <Input
                                value={form.RushTagNumber}
                                onChange={e => set('RushTagNumber', e.target.value)}
                                placeholder="Optional"
                            />
                        </Field>
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" outline onClick={onClose} disabled={loading}>Cancel</Button>
                <Button color="warning" onClick={submit} disabled={loading || blockUntilAllReceived} style={{ minWidth: '140px' }}>
                    {loading ? <Spinner size="sm" /> : <><i className="mdi mdi-check me-1" />Forward all</>}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

// ─── Deliver all (2-step, same field layout as Bag Finder deliver) ───────────

const deliverNowFormatted = () => moment().format('YYYY-MM-DD HH:mm');

export const CaseFileDeliverAllModal = ({ caseFile, onClose, onSuccess, themeType }) => {
    const targetBags = getBagsEligibleForForwardOrDeliverAll(caseFile);
    const blockUntilAllReceived = caseFileHasBagWithoutReceiveDate(caseFile);
    const [step, setStep] = useState(1);
    const [form, setForm] = useState({
        Date: deliverNowFormatted(),
        Name: '',
        Contact: '',
        Identity: '',
        Condition: '',
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const sigRef = useRef(null);

    const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrors(p => ({ ...p, [k]: undefined })); };

    useEffect(() => {
        setStep(1);
        setForm({
            Date: deliverNowFormatted(),
            Name: '',
            Contact: '',
            Identity: '',
            Condition: '',
        });
        setErrors({});
        sigRef.current?.clear();
    }, [caseFile?.id]);

    const deliverFlatpickrOpts = useMemo(
        () => mergeOperationalDateCaps({
            dateFormat: 'Y-m-d H:i',
            altInput: true,
            altFormat: 'F j, Y H:i',
            enableTime: true,
            time_24hr: true,
            defaultDate: new Date(),
        }),
        [caseFile?.id],
    );

    const datePickerValue =
        form.Date && moment(form.Date, 'YYYY-MM-DD HH:mm', true).isValid()
            ? moment(form.Date, 'YYYY-MM-DD HH:mm').toDate()
            : new Date();

    const validateStep1 = () => {
        const e = {};
        if (!form.Date) e.Date = 'Delivery date & time is required.';
        if (!form.Name?.trim()) e.Name = 'Recipient name is required.';
        if (!form.Identity?.trim()) e.Identity = 'ID / identity is required.';
        if (!form.Condition?.trim()) e.Condition = 'Condition is required.';
        return e;
    };

    const goToSign = () => {
        const e = validateStep1();
        if (Object.keys(e).length) { setErrors(e); return; }
        setStep(2);
    };

    const submit = async () => {
        if (sigRef.current?.isEmpty()) {
            notify('error', 'Please obtain the customer signature before confirming.');
            return;
        }
        setLoading(true);
        const signatureData = sigRef.current.toDataURL('image/png');
        const res = await deliverAllRequest({
            id: caseFile.id,
            Date: form.Date,
            Name: form.Name.trim(),
            Contact: form.Contact?.trim() || undefined,
            Identity: form.Identity.trim(),
            Condition: form.Condition.trim(),
            Signature: signatureData,
        });
        setLoading(false);
        if (res?.Status === 'success') {
            onSuccess(res);
        } else {
            notify('error', res?.Message || 'Something went wrong.');
        }
    };

    const sigPadBg = themeType === 'dark' ? '#1e2330' : '#fff';

    return (
        <Modal isOpen size="lg" centered>
            <ModalHeader toggle={onClose}>
                <i className="mdi mdi-hand-okay me-2 text-success" />
                Deliver all bags
                <span className="ms-3">
                    <span className={`badge rounded-pill me-1 ${step === 1 ? 'bg-success' : 'bg-light text-dark'}`}>1 Info</span>
                    <span className={`badge rounded-pill ${step === 2 ? 'bg-success' : 'bg-light text-dark'}`}>2 Signature</span>
                </span>
            </ModalHeader>

            {step === 1 && (
                <>
                    <ModalBody>
                        <div className="mb-3 small text-muted" style={{ marginTop: '-0.25rem' }}>
                            {caseFile?.file_reference && <span className="me-2 fw-semibold">{caseFile.file_reference}</span>}
                            {caseFile?.reference && <span>{caseFile.reference}</span>}
                            {caseFile?.type && <span className="ms-2 badge bg-secondary">{caseFile.type}</span>}
                        </div>
                        {blockUntilAllReceived && (
                            <Alert color="warning" className="mb-3 py-2">
                                Receive every bag on this case first. The server will not deliver until all bags have a receive date.
                            </Alert>
                        )}
                        <BulkBagsPreview
                            bags={targetBags}
                            themeType={themeType}
                            toBeAction="delivered"
                            emptyLabel="No bags in RECEIVED status — deliver will not change any bags (other statuses stay as they are)."
                        />
                        <Row>
                            <Col md={6}>
                                <Field label="Delivery date & time" required error={errors.Date}>
                                    <Flatpickr
                                        key={`case-deliver-dt-${caseFile?.id ?? 'x'}`}
                                        className={`form-control${errors.Date ? ' is-invalid' : ''}`}
                                        options={deliverFlatpickrOpts}
                                        value={datePickerValue}
                                        onChange={([d]) => set('Date', d ? moment(d).format('YYYY-MM-DD HH:mm') : '')}
                                        placeholder="Select date & time"
                                    />
                                </Field>
                            </Col>
                            <Col md={6}>
                                <Field label="Identity / ID number" required error={errors.Identity}>
                                    <Input
                                        className={errors.Identity ? 'is-invalid' : ''}
                                        value={form.Identity}
                                        onChange={e => set('Identity', e.target.value)}
                                        placeholder="Passport, ID card, etc."
                                    />
                                </Field>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Field label="Recipient name" required error={errors.Name}>
                                    <Input
                                        className={errors.Name ? 'is-invalid' : ''}
                                        value={form.Name}
                                        onChange={e => set('Name', e.target.value)}
                                        placeholder="Full name"
                                    />
                                </Field>
                            </Col>
                            <Col md={6}>
                                <Field label="Contact">
                                    <Input
                                        value={form.Contact}
                                        onChange={e => set('Contact', e.target.value)}
                                        placeholder="Phone / email"
                                    />
                                </Field>
                            </Col>
                        </Row>
                        <Field label="Condition" required error={errors.Condition}>
                            <ConditionSuggestionPills themeType={themeType} onPick={t => set('Condition', t)} />
                            <Input
                                className={errors.Condition ? 'is-invalid' : ''}
                                value={form.Condition}
                                onChange={e => set('Condition', e.target.value)}
                                placeholder="Describe bag condition"
                            />
                        </Field>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" outline onClick={onClose}>Cancel</Button>
                        <Button color="success" onClick={goToSign} disabled={blockUntilAllReceived} style={{ minWidth: '180px' }}>
                            <i className="mdi mdi-draw me-1" />Next: signature
                        </Button>
                    </ModalFooter>
                </>
            )}

            {step === 2 && (
                <>
                    <ModalBody>
                        <div className="mb-4 p-3 rounded" style={{ background: themeType === 'dark' ? '#2a2f3d' : '#f8f9fa', border: '1px solid', borderColor: themeType === 'dark' ? '#3d4354' : '#e9ecef' }}>
                            <div
                                className="small fw-semibold mb-2"
                                style={{ color: themeType === 'dark' ? '#adb5bd' : '#6c757d' }}
                            >
                                Bags to be delivered ({targetBags.length})
                            </div>
                            {targetBags.length > 0 ? (
                                <BagTagPills bags={targetBags} />
                            ) : (
                                <span className="small text-muted">—</span>
                            )}
                            <div className="fw-semibold mb-2 mt-3">{form.Name}</div>
                            <div className="small text-muted">{form.Identity}{form.Contact ? ` · ${form.Contact}` : ''}</div>
                            <div className="small text-muted mt-1">Condition: {form.Condition}</div>
                            <div className="small text-muted">Date & time: {moment(form.Date).format('MMM D, YYYY HH:mm')}</div>
                        </div>
                        <div className="text-center">
                            <p className="fw-semibold mb-2 small">Customer signature (applies to all listed bags)</p>
                            <div style={{ touchAction: 'none' }}>
                                <SignatureCanvas
                                    ref={sigRef}
                                    penColor={themeType === 'dark' ? '#e9ecef' : '#212529'}
                                    backgroundColor={sigPadBg}
                                    canvasProps={{ width: 320, height: 200, style: { border: `2px solid ${themeType === 'dark' ? '#3d4354' : '#dee2e6'}`, borderRadius: '8px', margin: '0 auto' } }}
                                />
                            </div>
                            <Button color="link" size="sm" className="mt-1 text-muted" onClick={() => sigRef.current?.clear()}>
                                <i className="mdi mdi-refresh me-1" />Clear
                            </Button>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" outline onClick={() => { setForm(f => ({ ...f, Date: deliverNowFormatted() })); setStep(1); }} disabled={loading}>Back</Button>
                        <Button color="success" onClick={submit} disabled={loading || blockUntilAllReceived} style={{ minWidth: '160px' }}>
                            {loading ? <Spinner size="sm" /> : <><i className="mdi mdi-check-circle me-1" />Confirm deliver all</>}
                        </Button>
                    </ModalFooter>
                </>
            )}
        </Modal>
    );
};
